import { useEffect } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, Chip, Stack, Typography } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import type { Vault } from 'src/pages/admin/services/vaultService';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';

import { VAULTS_NS as NS, VAULTS_PERMISSION_RESOURCE as RESOURCE } from './constants';
import {
  detailFigures,
  formatCount,
  formatPercent,
  operationsControl,
  showsClosure,
  showsManagerAddress,
  showsOperations,
  subscriptionsStatusKey,
} from './helpers';
import { ClosureBlock } from './ClosureBlock';
import { ClosureDialog } from './ClosureDialog';
import { FrozenBanner } from './FrozenBanner';
import { ManagerAddressDialog } from './ManagerAddressDialog';
import { OperationsDialog } from './OperationsDialog';
import { RiskBadgeChip, RiskBreachesCard } from './RiskBreaches';
import { useVaultClosure } from './useVaultClosure';
import { useVaultDetail } from './useVaultDetail';
import { useVaultFreeze } from './useVaultFreeze';
import { useVaultManagerAddress } from './useVaultManagerAddress';
import { useVaultOperations } from './useVaultOperations';
import { useVaultRiskBreaches } from './useVaultRiskBreaches';
import { VaultAddress } from './VaultAddress';
import { KindChip, StateChip } from './VaultChips';

const MONO = { fontFamily: 'monospace', overflowWrap: 'anywhere' } as const;

/** One label / value pair of a detail block. */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ typography: 'body2', fontVariantNumeric: 'tabular-nums' }}>{children}</Box>
  </Box>
);

const Block = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <Card sx={{ p: 3 }}>
    <Typography variant="subtitle1">{title}</Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {hint}
      </Typography>
    )}
    <Box
      sx={{
        mt: 2,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
      }}
    >
      {children}
    </Box>
  </Card>
);

/**
 * Module PRD §14.4 vault detail (XBIT-4239): the header, "Stake & capacity" and "Immutable terms", from the row the
 * list carries (`GET /vault/vaults/:id`). It shows what §14.4's detail table lists and nothing more: no share counts,
 * strategy, manager identity or timestamps (sources rule, specs !86). "Operations" (XBIT-4242) pauses and resumes
 * subscriptions on an ACTIVE or PAUSED vault. "Close vault" (XBIT-4243, §14.5) requests, approves and cancels a closure;
 * it is disabled with its reason while closing is shipped dark. An id the catalog does not know goes back to the list.
 */
const VaultDetail = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { vault, loading, loadError, notFound, refetch } = useVaultDetail(id);
  const t = (key: string, options?: object) => translate(`${NS}.detail.${key}`, options);

  useEffect(() => {
    if (!notFound) return;
    notify(translate(`${NS}.detail.notFound`, { vaultId: id ?? '' }), { type: 'warning' });
    navigate(paths.dashboard.vaultVaults.root, { replace: true });
    // translate and notify are not identity-stable; the redirect follows notFound alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound]);

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={vault?.name ?? id ?? ''}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`), href: paths.dashboard.vaultVaults.root },
            { name: vault?.name ?? id ?? '' },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refetch}
              disabled={loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          {loadError && <Alert severity="error">{loadError}</Alert>}
          <LoadingPanel isLoading={loading}>
            {vault && <DetailBody vault={vault} t={t} refetch={refetch} />}
          </LoadingPanel>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
};

const DetailBody = ({
  vault,
  t,
  refetch,
}: {
  vault: Vault;
  t: (key: string, options?: object) => string;
  refetch: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const f = detailFigures(vault);
  const operations = useVaultOperations({ vaultId: vault.vaultId, onSettled: refetch });
  const managerAddress = useVaultManagerAddress({ vaultId: vault.vaultId, onSettled: refetch });
  const closure = useVaultClosure({
    vaultId: vault.vaultId,
    closureId: vault.closure?.id,
    onSettled: refetch,
  });
  const reconciliationFreeze = useVaultFreeze(vault.vaultId);
  const control = operationsControl(vault);
  // XBIT-4764: one open-only read feeds the header badge and the Risk breaches card; "Show all" reads the rest.
  const risk = useVaultRiskBreaches(vault.vaultId);
  const table = (key: string) => translate(`${NS}.table.${key}`);
  return (
    <Stack spacing={3}>
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography variant="h5" sx={{ overflowWrap: 'anywhere' }}>
            {vault.name}
          </Typography>
          <KindChip kind={vault.kind} />
          <StateChip state={vault.state} />
          <RiskBadgeChip badge={risk.badge} />
        </Stack>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <Field label={t('vaultId')}>
            <Box component="span" sx={MONO}>
              {vault.vaultId}
            </Box>
          </Field>
          <Field label={t('vaultAddress')}>
            <VaultAddress address={vault.hlAddress} full />
          </Field>
        </Box>
      </Card>

      <Alert severity="info">{translate(`${NS}.snapshotHint`)}</Alert>

      {/* §14.4 "Stake & capacity": stake value, current ratio vs the floor, remaining headroom, TVL against the cap. */}
      <Block title={t('capacity')}>
        <Field label={t('stakeValue')}>{f.managerPresentValue}</Field>
        <Field label={t('managerRatio')}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{f.managerRatio}</span>
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
              {f.floorTier
                ? t('floorOfTier', { floor: f.floor, tier: f.floorTier })
                : t('floorOf', { floor: f.floor })}
            </Typography>
            {f.belowFloor && <Chip size="small" color="warning" label={t('belowFloor')} />}
          </Stack>
        </Field>
        <Field label={table('remaining')}>{f.remaining}</Field>
        <Field label={t('tvlAgainstCap')}>
          {f.tvl} / {f.sizeCap}
          <Typography
            component="div"
            variant="caption"
            sx={{ color: f.overCap ? 'error.main' : 'text.secondary' }}
          >
            {f.utilization}
          </Typography>
        </Field>
      </Block>

      {/* §14.4 "Operations" (XBIT-4242): only subscriptions can be paused; redemptions stay open and never pause. */}
      {showsOperations(vault) && (
        <Block title={t('operations.title')} hint={t('operations.hint')}>
          <Field label={t('operations.subscriptions')}>
            {t(`operations.status.${subscriptionsStatusKey(vault)}`)}
            {control === 'takeOver' && (
              <Typography component="div" variant="caption" sx={{ color: 'text.secondary' }}>
                {t('operations.managerPauseCaption')}
              </Typography>
            )}
          </Field>
          {control && (
            <Box sx={{ alignSelf: 'center' }}>
              <PermissionButton resource={RESOURCE} action="update">
                <Button
                  variant="outlined"
                  color={control === 'resume' ? 'primary' : 'warning'}
                  disabled={operations.busy}
                  onClick={() => operations.open(control)}
                >
                  {t(`operations.${control}`)}
                </Button>
              </PermissionButton>
            </Box>
          )}
        </Block>
      )}
      {/* §14.4 内部管理账号 (XBIT-4258): an official vault's manager address, changed by operations; the address only. */}
      {showsManagerAddress(vault) && (
        <Block title={t('managerAddress.title')} hint={t('managerAddress.note')}>
          <Field label={t('managerAddress.current')}>
            <VaultAddress address={vault.managerAddress} full />
          </Field>
          <Box sx={{ alignSelf: 'center' }}>
            <PermissionButton resource={RESOURCE} action="update">
              <Button
                variant="outlined"
                color="warning"
                disabled={managerAddress.busy}
                onClick={managerAddress.open}
              >
                {t('managerAddress.change')}
              </Button>
            </PermissionButton>
          </Box>
        </Block>
      )}
      {/* XBIT-4864: the reconciliation freeze (not the closure notice's); stays empty until the BFF serves the freeze read. */}
      <FrozenBanner freeze={reconciliationFreeze} vaultId={vault.vaultId} />
      {/* §14.5 关闭金库 (XBIT-4243): request, approve (a second operator) or cancel a closure; shipped dark until settlement exists. */}
      {showsClosure(vault) && (
        <Block title={t('closure.title')} hint={t('closure.hint')}>
          <ClosureBlock vault={vault} form={closure} />
        </Block>
      )}
      {/* §14.4 "Immutable terms": lock-up, profit share, minimum ownership, redemption method; read-only. */}
      <Block title={t('terms')} hint={t('termsHint')}>
        <Field label={t('lockupDays')}>{formatCount(vault.lockupDays)}</Field>
        <Field label={t('profitShare')}>{formatPercent(vault.feeRate)}</Field>
        <Field label={t('ownershipFloor')}>{f.ownershipFloor}</Field>
        <Field label={t('redemptionMethod')}>{vault.redemptionMethod}</Field>
      </Block>

      {/* XBIT-4764 / XBIT-4741: the after-fill monitor's breach episodes, read-only. */}
      <RiskBreachesCard risk={risk} />

      <OperationsDialog form={operations} vaultName={vault.name} />
      <ManagerAddressDialog form={managerAddress} vaultId={vault.vaultId} vaultName={vault.name} />
      <ClosureDialog form={closure} vaultName={vault.name} />
    </Stack>
  );
};

export default VaultDetail;
